import { PenTool, Terminal, Library } from "lucide-react";
import { Reveal } from "@/components/motion";
import { IMAGES, GCODE_SNIPPET } from "@/constants/site";

const Cell = ({ children, className = "", testId }) => (
  <div className={`grid-cell p-8 sm:p-10 ${className}`} data-testid={testId}>
    {children}
  </div>
);

export const Features = () => (
  <section id="features" className="px-6 sm:px-12 lg:px-20 py-24 max-w-[1400px] mx-auto" data-testid="features">
    <Reveal>
      <p className="label-mono mb-4 flex items-center gap-3">
        <span className="inline-block w-8 h-px bg-[#FF3B00]" /> 01 — What it does
      </p>
      <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95] max-w-2xl">
        One package. Shape editor, live plotter, G-code out.
      </h2>
    </Reveal>

    <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Reveal className="lg:col-span-2" delay={0.1}>
        <Cell testId="feature-plotter" className="h-full flex flex-col">
          <div className="flex items-center gap-3 mb-5">
            <PenTool className="w-5 h-5 text-[#FF3B00]" />
            <h3 className="font-display uppercase tracking-tight text-2xl sm:text-3xl">Real-time toolpath plotter</h3>
          </div>
          <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-md">
            Draw or load a shape and watch the cut path render as you edit — feeds, rapids and
            plunge moves drawn live, so mistakes show up on screen instead of in your stock.
          </p>
          <div className="mt-auto pt-8">
            <div className="border border-white/10 overflow-hidden bg-black">
              <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2 bg-[#0A0A0A]">
                <span className="w-2 h-2 rounded-full bg-[#FF3B00]" />
                <span className="w-2 h-2 rounded-full bg-[#F5A623]" />
                <span className="w-2 h-2 rounded-full bg-[#00FF41]" />
                <span className="ml-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/40">CNCRoutersoft — G-code Plotter</span>
              </div>
              <img src={IMAGES.plotter_ui} alt="CNCroutersoft G-code plotter with a rendered toolpath" className="w-full object-cover" />
            </div>
            <p className="mt-3 font-mono2 text-[11px] text-white/35 uppercase tracking-[0.2em]">Fig. 01 — the real plotter: every move previewed before the spindle spins</p>
          </div>
        </Cell>
      </Reveal>

      <Reveal delay={0.2}>
        <Cell testId="feature-gcode" className="h-full flex flex-col">
          <div className="flex items-center gap-3 mb-5">
            <Terminal className="w-5 h-5 text-[#FF3B00]" />
            <h3 className="font-display uppercase tracking-tight text-2xl sm:text-3xl">G-code export</h3>
          </div>
          <p className="text-white/70 text-sm leading-relaxed mb-6">
            One click writes clean, controller-ready G-code for your router.
          </p>
          <pre className="mt-auto bg-[#0A0A0A] border border-white/10 font-mono2 text-[11px] leading-relaxed text-[#00FF41] p-4 overflow-x-auto">{GCODE_SNIPPET}</pre>
        </Cell>
      </Reveal>

      <Reveal className="lg:col-span-3" delay={0.15}>
        <Cell testId="feature-shapelib" className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <Library className="w-5 h-5 text-[#FF3B00]" />
              <h3 className="font-display uppercase tracking-tight text-2xl sm:text-3xl">A shape library that stays yours</h3>
            </div>
            <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-lg">
              Every design lives in the local <span className="font-mono2 text-[#00FF41]">shapelib</span> folder —
              plain files you can back up, share, or version yourself. No cloud lock-in, no accounts
              inside the app.
            </p>
            <p className="mt-6 font-mono2 text-[11px] text-white/35 uppercase tracking-[0.2em]">
              Fig. 02 — Shape Editor: traces, slope markers and machine settings, all local
            </p>
          </div>
          <div className="border border-white/10 overflow-hidden bg-black">
            <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2 bg-[#0A0A0A]">
              <span className="w-2 h-2 rounded-full bg-[#FF3B00]" />
              <span className="w-2 h-2 rounded-full bg-[#F5A623]" />
              <span className="w-2 h-2 rounded-full bg-[#00FF41]" />
              <span className="ml-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/40">CNCRoutersoft — Shape Editor</span>
            </div>
            <img src={IMAGES.shapelib_bg} alt="CNCroutersoft Shape Editor with a shape, traces list and controls" className="w-full object-cover" />
          </div>
        </Cell>
      </Reveal>
    </div>
  </section>
);
